import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import BtSparkline from '../bt-sparkline/bt-sparkline';
import { SupplierSummary } from '../../services/service-evaluations';
import { EvaluationLimits } from '../../util/evaluationContract';
import { formatScore, trendDelta } from '../../util/evaluationFormat';
import { trendTone } from '../../util/evaluationStatusMeta';
import { formatStockholmDay, formatTrendLabel } from './reviewFormat';
import styles from './page-supplier-evaluations.module.scss';

const PILL_ICON = { fontSize: 14 };

type Props = {
  points: SupplierSummary['trend'];
  formatDay?: (instant: string) => string;
};

const Trend: FC<Props> = ({ points, formatDay = formatStockholmDay }) => {
  const [t] = useTranslation('evaluations');
  const change = trendDelta(points);
  const title = <p className={styles.label}>{t('review.trend.title', 'Betyg över tid')}</p>;

  if (!change) {
    return (
      <>
        {title}
        <span className={`${styles.muted} ${styles.small}`}>
          {t('review.trend.tooFew', 'Trend visas från tre svar.')}
        </span>
      </>
    );
  }

  const tone = trendTone(change.delta);

  return (
    <>
      <div className={styles.trendHeader}>
        {title}
        <span
          className={styles.trendPill}
          style={{ background: tone.backgroundColor, color: tone.color }}
        >
          {change.delta < 0 ? (
            <TrendingDownIcon style={PILL_ICON} aria-hidden />
          ) : (
            <TrendingUpIcon style={PILL_ICON} aria-hidden />
          )}
          {formatTrendLabel(change)}
        </span>
      </div>
      <BtSparkline
        values={points.map(point => point.score)}
        labels={points.map(
          point => `${formatScore(point.score)} · ${formatDay(point.submittedAt)}`,
        )}
        min={EvaluationLimits.ScaleMin}
        max={EvaluationLimits.ScaleMax}
        gridLines={[1, 3, 5]}
        startLabel={formatDay(points[0].submittedAt)}
        endLabel={formatDay(points[points.length - 1].submittedAt)}
        width={640}
        height={72}
        fluid
      />
    </>
  );
};

export default Trend;
